import type { ConversationMessageDTO } from '@eriko/shared-types';
import { ChatAttachment, ChatAvatar, ChatBubble, ChatMeta } from '@eriko/ui/src/components/ui/chat';
import { cn } from '@eriko/ui/src/lib/utils';
import { cjk } from '@streamdown/cjk';
import { code } from '@streamdown/code';
import { math } from '@streamdown/math';
import { mermaid } from '@streamdown/mermaid';
import { Streamdown } from 'streamdown';
import type { ComponentProps } from 'react';
import type { PublicUser } from './profileCache';

type StreamdownComponents = ComponentProps<typeof Streamdown>['components'];

function preprocessEntityLinks(content: string): string {
  return content.replace(/\]\(entity:([^)]+)\)/g, '](#entity-$1)');
}

function stripAttachedFilesBlock(content: string): string {
  return content.replace(/\n?\n?\[Attached files:\n[\s\S]*?\n\]\s*$/, '').trim();
}

interface AttachedFile {
  fileName: string;
  fileType: string;
  url?: string;
}

function extractFilesFromContent(content: string): AttachedFile[] {
  const match = content.match(/\[Attached files:\n([\s\S]*?)\n\]/);
  if (!match?.[1]) return [];
  return match[1]
    .split('\n')
    .map((line): AttachedFile | null => {
      const m = line.match(/^- (.+?) \((\w+)\)(?:\s*(?:→|->)\s*(\S+))?/);
      if (!m?.[1]) return null;
      return { fileName: m[1], fileType: m[2] ?? '', url: m[3] };
    })
    .filter((f): f is AttachedFile => f !== null);
}

const proseClasses =
  'text-sm leading-relaxed text-surface-900 prose prose-sm max-w-none [&>p]:mb-2 [&>p:last-child]:mb-0 [&>ul]:list-disc [&>ul]:pl-4 [&>ol]:list-decimal [&>ol]:pl-4 [&>table]:w-full [&>table]:border-collapse [&_th]:bg-surface-100 [&_th]:px-3 [&_th]:py-1.5 [&_th]:text-left [&_th]:text-xs [&_th]:font-semibold [&_th]:border [&_th]:border-surface-200 [&_td]:px-3 [&_td]:py-1.5 [&_td]:border [&_td]:border-surface-200 [&_td]:text-sm';

interface MessageRowProps {
  msg: ConversationMessageDTO;
  profiles: Record<string, PublicUser>;
  mdComponents?: StreamdownComponents;
}

export function MessageRow({ msg, profiles, mdComponents }: MessageRowProps) {
  const meta = (msg.metadata as Record<string, unknown> | null) ?? null;
  const isInternal = meta?.internalNote === true;
  const forkNote = msg.senderType === 'AI' && meta?.forkNote;

  if (forkNote) {
    return (
      <div className="self-center text-[11px] italic text-surface-500">
        ↳ Moved &ldquo;{msg.content}&rdquo; →{' '}
        <span className="underline">{String(meta?.toTitle ?? '')}</span>
      </div>
    );
  }

  const profile = msg.senderId ? profiles[msg.senderId] : null;

  switch (msg.senderType) {
    case 'SME': {
      const name = profile?.fullName || 'Client';
      const files = extractFilesFromContent(msg.content);
      const displayContent = stripAttachedFilesBlock(msg.content);
      return (
        <div className="flex gap-2">
          <div className="mt-5 w-7 h-7 rounded-full bg-pos-surface text-pos border border-pos-border flex items-center justify-center text-[10px] font-bold shrink-0">
            {profile?.initials || 'CL'}
          </div>
          <div className="max-w-[75%]">
            <div className="flex items-center gap-1.5 text-[10.5px] text-surface-500 mb-1">
              {name}
              <span className="text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded bg-pos-surface border border-pos-border text-pos">
                Client
              </span>
            </div>
            <div className="bg-surface-0 border border-surface-200 rounded-lg px-3 py-2 text-sm">
              {displayContent && (
                <p className="whitespace-pre-wrap">{displayContent}</p>
              )}
              {files.length > 0 && (
                <div
                  className={cn(
                    'flex flex-wrap gap-1.5',
                    displayContent && 'mt-2 pt-2 border-t border-surface-200',
                  )}
                >
                  {files.map((f, i) =>
                    f.url ? (
                      <a
                        key={i}
                        href={f.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="no-underline"
                      >
                        <ChatAttachment fileName={f.fileName} />
                      </a>
                    ) : (
                      <ChatAttachment key={i} fileName={f.fileName} />
                    ),
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    case 'REVIEWER': {
      const name = profile?.fullName || 'Bookkeeper';
      const files = extractFilesFromContent(msg.content);
      const displayContent = stripAttachedFilesBlock(msg.content);
      return (
        <div className="flex justify-end gap-2">
          <div className="max-w-[75%]">
            <div className="text-[10.5px] text-surface-500 text-right mb-1">
              {name} · <span className="opacity-70">Bookkeeper</span>
              {isInternal && (
                <span className="ml-2 text-warn font-semibold">[Internal note]</span>
              )}
            </div>
            <div
              className={cn(
                'rounded-lg px-3 py-2 text-sm',
                isInternal
                  ? 'bg-warn-surface border border-warn-border text-surface-900'
                  : 'bg-surface-900 text-surface-0',
              )}
            >
              {displayContent && (
                <p className="whitespace-pre-wrap">{displayContent}</p>
              )}
              {files.length > 0 && (
                <div
                  className={cn(
                    'flex flex-wrap gap-1.5',
                    displayContent && 'mt-2 pt-2',
                    displayContent && (isInternal ? 'border-t border-warn-border' : 'border-t border-white/15'),
                  )}
                >
                  {files.map((f, i) =>
                    f.url ? (
                      <a
                        key={i}
                        href={f.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="no-underline"
                      >
                        <ChatAttachment fileName={f.fileName} dark={!isInternal} />
                      </a>
                    ) : (
                      <ChatAttachment key={i} fileName={f.fileName} dark={!isInternal} />
                    ),
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="mt-5 w-7 h-7 rounded-full bg-surface-900 text-surface-0 flex items-center justify-center text-[10px] font-bold shrink-0">
            {profile?.initials || 'BK'}
          </div>
        </div>
      );
    }

    case 'AI':
      return (
        <div className="flex gap-2">
          <ChatAvatar variant="bot" className="mt-5" />
          <div className="max-w-[75%]">
            <ChatMeta name="Eriko" tag="Bot" tagVariant="bot" time={msg.createdAt} />
            <ChatBubble variant="assistant">
              <div className={proseClasses}>
                <Streamdown
                  mode="static"
                  plugins={{ code, math, mermaid, cjk }}
                  components={mdComponents}
                >
                  {preprocessEntityLinks(msg.content)}
                </Streamdown>
              </div>
            </ChatBubble>
          </div>
        </div>
      );

    default:
      return null;
  }
}
